import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import { useShop } from "../contexts/ShopContext";
import { useAuth } from "../contexts/AuthContext";
import { useSettings } from "../contexts/SettingsContext";
import { toast } from "sonner";
import { formatPrice } from "../lib/format";
import { useAddressAutocomplete } from "../lib/googlePlaces";
import { usePincodeLookup } from "../lib/pincodeLookup";
import { trackBeginCheckout, trackPurchase } from "../lib/analytics";
import { Shield, Smartphone, CreditCard, Wallet, Landmark as LandmarkIcon, MapPin, Search } from "lucide-react";

const PHONE_RE = /^[6-9]\d{9}$/;
const PIN_RE = /^\d{6}$/;

// Defined OUTSIDE Checkout so React keeps the same component instance between renders
// — prevents focus loss / DOM recreation on every keystroke.
function Field({ name, label, type = "text", placeholder, value, onChange, autoFocus, autoComplete, full, error, ...rest }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="text-[10px] uppercase tracking-[0.2em] text-muted block mb-1.5">{label}</label>
      <input
        className={`input-line ${error ? "!border-b-red-400" : ""}`}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        data-testid={`ship-${name}`}
        {...rest}
      />
      {error && <p className="text-[10px] text-red-400 mt-1 uppercase tracking-[0.15em]" data-testid={`error-${name}`}>{error}</p>}
    </div>
  );
}

export default function Checkout() {
  const { cart, subtotal, discount, shippingFee, total, coupon, setCoupon, clearCart } = useShop();
  const { user, refresh: refreshUser } = useAuth();
  const { settings } = useSettings();
  const nav = useNavigate();
  const searchRef = useRef(null);

  const [ship, setShip] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    flat_no: "",
    address_line1: "",
    landmark: "",
    area: "",
    city: "",
    state: "",
    zip: "",
    country: "India",
  });
  const [errors, setErrors] = useState({});
  const [autoFilled, setAutoFilled] = useState(false);
  const [saveAddress, setSaveAddress] = useState(true);
  const [couponCode, setCouponCode] = useState(coupon?.code || "");
  const [eligible, setEligible] = useState([]);
  const [busy, setBusy] = useState(false);
  const [config, setConfig] = useState(null);
  const [pin, setPin] = useState(null);
  const [checkingPin, setCheckingPin] = useState(false);

  // GA4 begin_checkout once per cart change — also log a checkout_started per line item
  // so the admin Engagement funnel can attribute drop-offs at product level
  useEffect(() => {
    if (cart.length > 0) {
      trackBeginCheckout(cart, total, coupon?.code);
      cart.forEach(item => {
        api.post("/events", { name: "checkout_started", props: {
          product_id: item.product_id, name: item.name, price: item.price, qty: item.quantity,
        }}).catch(() => {});
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.length]);

  useEffect(() => {
    api.get("/payments/config").then(({ data }) => setConfig(data)).catch(() => setConfig({ enabled: false }));
  }, []);

  // Load ALL eligible coupons for this cart so the shopper can pick one at checkout.
  // Same endpoint the cart drawer uses. Hides once a coupon is already applied.
  useEffect(() => {
    if (cart.length === 0 || coupon) { setEligible([]); return; }
    const items = cart.map(i => ({ product_id: i.product_id, price: i.price, quantity: i.quantity }));
    api.post("/coupons/auto-apply", { code: "", subtotal, items })
      .then(({ data }) => setEligible(data.eligible || []))
      .catch(() => setEligible([]));
  }, [cart, subtotal, coupon]);

  // Auto serviceability check
  useEffect(() => {
    if (!PIN_RE.test(ship.zip)) { setPin(null); return; }
    setCheckingPin(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/shipping/serviceability", { pincode: ship.zip });
        setPin(data);
      } catch (e) {
        setPin({ serviceable: false, error: formatErr(e) });
      } finally { setCheckingPin(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [ship.zip]);

  const set = (k, v) => { setShip(s => ({ ...s, [k]: v })); setErrors(e => ({ ...e, [k]: "" })); };

  const { ready: gReady, available: gAvailable } = useAddressAutocomplete(searchRef, (parsed) => {
    setShip(s => ({
      ...s,
      address_line1: parsed.address_line1 || parsed.formatted_address || s.address_line1,
      area: parsed.area || s.area,
      city: parsed.city || s.city,
      state: parsed.state || s.state,
      zip: parsed.zip || s.zip,
      country: parsed.country || "India",
    }));
    setAutoFilled(true);
    toast.success("Address auto-filled — add your flat / house number to complete");
  });

  // PIN-code → auto-fill city/state/area. Only fills BLANK fields so we never
  // overwrite what the customer typed. Fires when the ZIP hits exactly 6 valid digits.
  const { loading: pinLoading, error: pinError } = usePincodeLookup(ship.zip, (result) => {
    setShip(s => ({
      ...s,
      city: s.city.trim() || result.city,
      state: s.state.trim() || result.state,
      area: s.area.trim() || result.area,
      country: s.country || result.country,
    }));
    setAutoFilled(true);
    toast.success(`PIN ${ship.zip} → ${result.city}, ${result.state}`);
  });

  const validate = () => {
    const e = {};
    if (!ship.name.trim()) e.name = "Required";
    if (!/^\S+@\S+\.\S+$/.test(ship.email)) e.email = "Valid email required";
    if (!PHONE_RE.test(ship.phone)) e.phone = "10-digit Indian mobile (starts 6/7/8/9)";
    if (!ship.flat_no.trim()) e.flat_no = "Flat / house number required";
    if (!ship.address_line1.trim()) e.address_line1 = "Address required";
    if (!ship.city.trim()) e.city = "City required";
    if (!ship.state.trim()) e.state = "State required";
    if (!PIN_RE.test(ship.zip)) e.zip = "6-digit PIN code required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const applyCoupon = async () => {
    if (!couponCode) return;
    try {
      const items = cart.map(i => ({ product_id: i.product_id, price: i.price, quantity: i.quantity }));
      const { data } = await api.post("/coupons/validate", {
        code: couponCode.toUpperCase(),
        subtotal,
        user_email: user?.email,
        items,
      });
      setCoupon(data);
      const summary = data.type === "bxgy"
        ? `Buy ${data.buy_qty} Get ${data.get_qty} Free`
        : data.type === "flat_off"
          ? `₹${data.flat_off} off`
          : `${data.discount_percent}% off`;
      toast.success(`Coupon applied — ${summary}`);
    } catch (err) { setCoupon(null); toast.error(formatErr(err)); }
  };

  const applyEligible = (c) => {
    setCoupon(c);
    setCouponCode(c.code);
    toast.success(`Coupon applied — ${c.label || c.code}`);
  };

  const payNow = async () => {
    if (!user) { toast.error("Please sign in to checkout"); nav("/login?next=/checkout"); return; }
    if (cart.length === 0) { toast.error("Your bag is empty"); return; }
    if (!validate()) { toast.error("Please complete the form"); return; }
    if (!window.Razorpay || !config?.enabled) { toast.error("Payment gateway not ready"); return; }
    if (pin && pin.configured && !pin.serviceable) { toast.error("Sorry — we can't deliver to this PIN code yet"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/razorpay/order", {
        items: cart.map(i => ({ product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity, size: i.size, image: i.image })),
        shipping: ship,
        coupon_code: coupon?.code || null,
        subtotal, discount, total,
      });
      // Server-authoritative amount — the browser only shows what the server priced.
      // If server recomputed a different total (product price changed / coupon invalid),
      // surface it to the shopper before Razorpay modal opens.
      if (data.server_total && Math.abs(data.server_total - total) > 0.5) {
        toast.error(`Price updated — total is now ${formatPrice(data.server_total)}. Please review your bag.`, { duration: 8000 });
      }
      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.razorpay_order_id,
        name: settings.brand_name || "GOSHI",
        description: `Order · ${cart.length} item${cart.length > 1 ? "s" : ""}`,
        image: settings.logo_url || undefined,
        prefill: { name: ship.name, email: ship.email, contact: ship.phone },
        notes: { pending_order_id: data.pending_order_id },
        theme: { color: "#C8A97E" },
        handler: async (response) => {
          try {
            const { data: order } = await api.post("/payments/razorpay/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              pending_order_id: data.pending_order_id,
              save_address: saveAddress,
            });
            trackPurchase(order);
            clearCart();
            // Refresh /auth/me so the newly-linked phone/name shows in Profile immediately
            refreshUser().catch(() => {});
            nav(`/order/${order.order_id}`, { state: { order } });
          } catch (err) {
            toast.error(`Payment verification failed: ${formatErr(err)}`);
          }
        },
        modal: { ondismiss: () => { setBusy(false); toast.message("Payment cancelled"); } },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (r) => { toast.error(`Payment failed: ${r.error?.description || "Try again"}`); setBusy(false); });
      rzp.open();
    } catch (err) { toast.error(formatErr(err)); setBusy(false); }
  };

  if (cart.length === 0) {
    return (
      <div className="pt-32 pb-24 px-6 md:px-12 min-h-screen text-center">
        <h1 className="font-serif text-4xl">Your bag is empty</h1>
        <Link to="/shop" className="btn-outline mt-8 inline-block">Continue Shopping</Link>
      </div>
    );
  }

  return (
    <div className="pt-28 pb-24 px-4 md:px-12">
      <div className="max-w-6xl mx-auto">
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Secure Checkout</p>
        <h1 className="font-serif text-4xl md:text-5xl mb-10">Checkout</h1>

        <div className="grid lg:grid-cols-[1fr_420px] gap-10">
          {/* LEFT: form */}
          <div className="space-y-10">
            {/* Contact */}
            <section className="space-y-5">
              <h2 className="text-xs uppercase tracking-[0.25em] text-gold">Contact</h2>
              {!user && (
                <div className="border border-[rgba(200,169,126,0.25)] bg-[rgba(200,169,126,0.04)] p-4 flex items-center justify-between gap-4 flex-wrap" data-testid="checkout-guest-signin">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.22em] text-gold">Have an account?</p>
                    <p className="text-xs text-muted mt-1">Sign in for faster checkout with saved addresses.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                      sessionStorage.setItem("goshi_auth_next", "/checkout");
                      const redirectUrl = window.location.origin + "/auth/callback";
                      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                    }}
                    className="btn-outline text-xs"
                    data-testid="checkout-google-signin-btn"
                  >
                    Continue with Google
                  </button>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-5">
                <Field name="name" label="Full Name" value={ship.name} onChange={(v) => set("name", v)} autoComplete="name" autoFocus error={errors.name} />
                <Field name="email" label="Email" type="email" value={ship.email} onChange={(v) => set("email", v)} autoComplete="email" error={errors.email} />
                <Field name="phone" label="Mobile (10-digit)" type="tel" value={ship.phone} onChange={(v) => set("phone", v.replace(/\D/g, "").slice(0, 10))} autoComplete="tel" placeholder="9876543210" error={errors.phone} />
              </div>
            </section>

            {/* Delivery Address */}
            <section className="space-y-5">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h2 className="text-xs uppercase tracking-[0.25em] text-gold">Delivery Address</h2>
                {gAvailable && <span className="text-[10px] uppercase tracking-[0.18em] text-muted/70 flex items-center gap-1.5"><MapPin size={11} className="text-gold" /> Google Places enabled</span>}
              </div>

              {gAvailable && (
                <div className="relative">
                  <Search size={14} strokeWidth={1.5} className="absolute left-0 top-1/2 -translate-y-1/2 text-gold pointer-events-none z-10" />
                  <div ref={searchRef} className="pl-7 google-places-slot" data-testid="address-search-container" />
                  <p className="text-[10px] uppercase tracking-[0.18em] text-muted/70 mt-2">
                    {gReady
                      ? "Start typing to find your address — we'll fill in city, state, PIN automatically."
                      : "Loading Google Places…"}
                  </p>
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-5">
                <Field name="flat_no" label="Flat / House No." value={ship.flat_no} onChange={(v) => set("flat_no", v)} placeholder="e.g. Flat 302, Tower B" autoComplete="address-line2" error={errors.flat_no} />
                <Field name="landmark" label="Landmark (optional)" value={ship.landmark} onChange={(v) => set("landmark", v)} placeholder="Near Phoenix Mall" error={errors.landmark} />
                <Field full name="address_line1" label="Street / Building / Society" value={ship.address_line1} onChange={(v) => { set("address_line1", v); setAutoFilled(false); }} autoComplete="address-line1" error={errors.address_line1} />
                <Field name="area" label="Area / Locality" value={ship.area} onChange={(v) => set("area", v)} error={errors.area} />
                <Field name="city" label="City" value={ship.city} onChange={(v) => set("city", v)} autoComplete="address-level2" error={errors.city} />
                <Field name="state" label="State" value={ship.state} onChange={(v) => set("state", v)} autoComplete="address-level1" error={errors.state} />
                <div className="relative">
                  <Field name="zip" label="PIN Code" value={ship.zip} onChange={(v) => set("zip", v.replace(/\D/g, "").slice(0, 6))} autoComplete="postal-code" error={errors.zip} />
                  {pinLoading && <span className="absolute right-0 top-6 text-[10px] uppercase tracking-[0.18em] text-gold animate-pulse" data-testid="pin-loading">Looking up…</span>}
                  {pinError && !pinLoading && <span className="absolute right-0 top-6 text-[10px] tracking-[0.15em] text-red-400" data-testid="pin-error">{pinError}</span>}
                </div>
                <Field name="country" label="Country" value={ship.country} onChange={(v) => set("country", v)} autoComplete="country-name" error={errors.country} />
              </div>

              {autoFilled && (
                <p className="text-[10px] uppercase tracking-[0.18em] text-gold">✓ Address auto-filled — please add your flat / house number to complete delivery</p>
              )}

              {ship.zip.length === 6 && (
                <div className="text-xs uppercase tracking-[0.18em]" data-testid="pin-check">
                  {checkingPin && <span className="text-muted">Checking delivery to {ship.zip}…</span>}
                  {!checkingPin && pin?.configured && pin?.serviceable && <span className="text-gold">✓ Deliverable{pin.etd ? ` · ETA ${pin.etd}` : ""}{pin.courier_count ? ` · ${pin.courier_count} couriers` : ""}</span>}
                  {!checkingPin && pin?.configured && !pin?.serviceable && <span className="text-red-400">✗ We're not delivering to {ship.zip} yet</span>}
                  {!checkingPin && pin && !pin.configured && <span className="text-muted/70">PIN noted (delivery check unavailable)</span>}
                </div>
              )}

              {user && (
                <label className="flex items-center gap-3 text-xs text-muted cursor-pointer select-none pt-2" data-testid="save-address-toggle">
                  <input
                    type="checkbox"
                    checked={saveAddress}
                    onChange={(e) => setSaveAddress(e.target.checked)}
                    className="w-4 h-4 accent-[#C8A97E]"
                    data-testid="save-address-checkbox"
                  />
                  Save this address for future orders
                </label>
              )}
            </section>

            {/* Payment */}
            <section className="space-y-5">
              <h2 className="text-xs uppercase tracking-[0.25em] text-gold">Payment</h2>
              <p className="text-xs text-muted flex items-center gap-2"><Shield size={12} className="text-gold" /> Secured by Razorpay · {config?.mode === "live" ? "Live" : "Test"} Mode · 256-bit SSL</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  { icon: Smartphone, label: "UPI", sub: "GPay · PhonePe · Paytm · BHIM" },
                  { icon: CreditCard, label: "Cards", sub: "Visa · Mastercard · RuPay · Amex" },
                  { icon: LandmarkIcon, label: "Netbanking", sub: "All major Indian banks" },
                  { icon: Wallet, label: "Wallets", sub: "Paytm · Mobikwik · Freecharge" },
                ].map(m => (
                  <div key={m.label} className="flex items-center gap-3 border border-[rgba(244,244,240,0.08)] p-4">
                    <m.icon size={20} strokeWidth={1.5} className="text-gold" />
                    <div>
                      <p className="text-sm">{m.label}</p>
                      <p className="text-xs text-muted">{m.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-muted/70">Choose your method in the Razorpay window when you click Pay.</p>
            </section>
          </div>

          {/* RIGHT: order summary (sticky on desktop, accordion on mobile) */}
          <aside className="bg-[#141414] p-6 lg:p-8 space-y-5 lg:sticky lg:top-28 self-start">
            <h3 className="text-xs uppercase tracking-[0.25em] text-gold">Order Summary · {cart.length} {cart.length === 1 ? "item" : "items"}</h3>
            <ul className="space-y-4 max-h-72 overflow-y-auto pr-1">
              {cart.map(i => (
                <li key={`${i.product_id}-${i.size}`} className="flex gap-3">
                  <div className="relative">
                    <img src={i.image} alt="" className="w-14 h-20 object-cover" />
                    <span className="absolute -top-2 -right-2 bg-[#C8A97E] text-[#0C0C0C] text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-medium">{i.quantity}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-sm truncate">{i.name}</p>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-muted mt-1">Size {i.size}</p>
                  </div>
                  <p className="text-sm text-gold whitespace-nowrap">{formatPrice(i.price * i.quantity)}</p>
                </li>
              ))}
            </ul>

            <div className="pt-4 border-t border-[rgba(244,244,240,0.06)] space-y-3">
              {!coupon && eligible.length > 0 && (
                <div className="border border-[rgba(200,169,126,0.25)] bg-[rgba(200,169,126,0.05)] p-3 space-y-2" data-testid="eligible-coupons">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-gold">
                    {eligible.length} offer{eligible.length > 1 ? "s" : ""} available for your bag
                  </p>
                  <div className="space-y-1.5">
                    {eligible.map(c => (
                      <button
                        key={c.coupon_id || c.code}
                        type="button"
                        onClick={() => applyEligible(c)}
                        className="w-full flex items-center justify-between gap-3 text-xs bg-[#0C0C0C] px-3 py-2.5 hover:bg-[#141414] border border-[rgba(244,244,240,0.06)] hover:border-[#C8A97E] transition-colors"
                        data-testid={`eligible-coupon-${c.code}`}
                      >
                        <div className="text-left">
                          <span className="text-gold font-mono">{c.code}</span>
                          <span className="text-muted ml-2">· {c.label || `${c.discount_percent}% off`}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-muted text-[10px] uppercase tracking-[0.18em]">Save {formatPrice(c.discount_amount)}</span>
                          <span className="text-gold text-[10px] uppercase tracking-[0.18em]">Apply →</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <input placeholder="Coupon code" value={couponCode} onChange={e => setCouponCode(e.target.value)} className="input-line flex-1 !py-2" data-testid="coupon-input" />
                <button type="button" onClick={applyCoupon} className="btn-outline !px-4 !py-2 text-[10px]" data-testid="apply-coupon-btn">Apply</button>
              </div>
              {coupon && (
                <div className="flex items-center justify-between gap-2" data-testid="applied-coupon-label">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-gold">
                    {coupon.code} · {coupon.type === "bxgy"
                      ? `Buy ${coupon.buy_qty} Get ${coupon.get_qty} Free`
                      : coupon.type === "flat_off"
                        ? `₹${coupon.flat_off} off`
                        : `${coupon.discount_percent}% off`}
                  </p>
                  <button
                    type="button"
                    onClick={() => { setCoupon(null); setCouponCode(""); }}
                    className="text-[10px] uppercase tracking-[0.18em] text-muted hover:text-gold"
                    data-testid="remove-coupon-checkout"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>

            <div className="space-y-1.5 text-sm pt-4 border-t border-[rgba(244,244,240,0.06)]">
              <Row label="Subtotal" value={formatPrice(subtotal)} />
              {discount > 0 && <Row label="Discount" value={`− ${formatPrice(discount)}`} accent />}
              <Row
                label="Shipping"
                value={shippingFee > 0 ? formatPrice(shippingFee) : "Complimentary"}
                testid="shipping-row"
              />
              {shippingFee > 0 && (
                <p className="text-[10px] uppercase tracking-[0.18em] text-muted/70 mt-1" data-testid="shipping-threshold-hint">
                  Add {formatPrice(1000 - Math.max(0, subtotal - discount))} more for complimentary shipping
                </p>
              )}
              <div className="pt-3 mt-1 border-t border-[rgba(244,244,240,0.06)] flex justify-between items-baseline">
                <span className="text-[10px] uppercase tracking-[0.2em] text-gold">Total</span>
                <span className="font-serif text-2xl text-gold" data-testid="order-total">{formatPrice(total)}</span>
              </div>
            </div>

            <button
              type="button"
              onClick={payNow}
              disabled={busy || !config?.enabled || !!(pin?.configured && !pin?.serviceable)}
              className="btn-gold w-full"
              data-testid="pay-now-btn"
            >
              {busy ? "Processing…" : `Pay ${formatPrice(total)} →`}
            </button>
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted/70 text-center">UPI · Cards · Netbanking · Wallets</p>
          </aside>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, accent, testid }) {
  return (
    <div className="flex justify-between" data-testid={testid}>
      <span className="text-muted text-[10px] uppercase tracking-[0.2em]">{label}</span>
      <span className={`text-sm ${accent ? "text-gold" : ""}`}>{value}</span>
    </div>
  );
}
